import { area } from "@turf/area";
import { polygon as turfPolygon } from "@turf/helpers";

export interface LatLng {
    lat: number;
    lng: number;
}

export const SQUARE_METRES_PER_ACRE = 4046.8564224;

export const isLatLng = (point: Partial<LatLng>): point is LatLng =>
    typeof point.lat === "number" &&
    Number.isFinite(point.lat) &&
    typeof point.lng === "number" &&
    Number.isFinite(point.lng);

/** Geodesic area of one closed ring, in acres. Undefined when the ring has no usable coordinates. */
export const getAreaAcres = (points: readonly Partial<LatLng>[] | undefined): number | undefined => {
    // A ring missing a coordinate has no area to speak of, rather than a wrong one.
    if (!points || points.length < 3 || !points.every(isLatLng)) return undefined;
    const ring = points.map(({ lat, lng }) => [lng, lat]);
    ring.push([...ring[0]]);
    const squareMetres = area(turfPolygon([ring]));
    return Number.isFinite(squareMetres) && squareMetres > 0 ? squareMetres / SQUARE_METRES_PER_ACRE : undefined;
};

/** Undefined when none of the areas is known. */
export const sumAreaAcres = (areas: readonly (number | undefined)[]): number | undefined => {
    const known = areas.filter((value): value is number => value !== undefined);
    return known.length ? known.reduce((sum, value) => sum + value, 0) : undefined;
};

/** A mapped boundary vertex as returned by the API or stored on a zone or AOI. */
export type MapVertex = { lat?: number | null; lng?: number | null };

export type PolygonRings = readonly (readonly MapVertex[])[];

/** Sums every polygon ring on a zone or area of interest. Undefined when no ring has a usable boundary. */
export const getAreaAcresFromPolygons = (polygons: PolygonRings | undefined): number | undefined =>
    sumAreaAcres(
        (polygons ?? []).map((ring) =>
            getAreaAcres(ring.map((vertex) => ({ lat: vertex.lat ?? undefined, lng: vertex.lng ?? undefined }))),
        ),
    );
